import { z } from 'zod';

export const RESOURCE_TYPES = [
    "People",
    "Technology",
    "Facilities",
    "Information",
    "Suppliers",
    "Equipment",
    "Communication",
    "Financial"
];

export const BIA_RESOURCE_TYPES = [
    "personnel",
    "applications",
    "itInfrastructure",
    "data",
    "communications",
    "officeSupplies",
    "facility",
    "legal",
    "externalServices",
];

const BIA_HORIZON_ALIASES = [
    ["t2h", "req2h"],
    ["t8h", "req8h"],
    ["t24h", "req24h"],
    ["t2d", "req2d"],
    ["t1w", "req1w"],
];

function emptyToUndefined(value) {
    if (value === "" || value === null || value === undefined) return undefined;
    if (typeof value === "number" && Number.isNaN(value)) return undefined;
    return value;
}

function toOptionalNumber(value) {
    const next = emptyToUndefined(value);
    if (next === undefined) return undefined;
    if (typeof next === "number") return Number.isFinite(next) ? next : undefined;
    if (typeof next !== "string") return undefined;
    const trimmed = next.trim();
    if (trimmed === "") return undefined;
    const asNumber = Number(trimmed);
    return Number.isFinite(asNumber) ? asNumber : undefined;
}

function toQuantityOrLabel(value) {
    const next = emptyToUndefined(value);
    if (next === undefined) return undefined;
    if (typeof next === "number") return Number.isFinite(next) ? next : undefined;
    if (typeof next !== "string") return next;
    const trimmed = next.trim();
    if (trimmed === "") return undefined;
    const asNumber = Number(trimmed);
    return Number.isFinite(asNumber) ? asNumber : trimmed;
}

function isFiniteNumberString(value) {
    if (typeof value !== "string") return false;
    const trimmed = value.trim();
    if (trimmed === "") return false;
    return Number.isFinite(Number(trimmed));
}

function applyBiaFieldAliases(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) return value;
    const next = { ...value };
    for (const [alias, canonical] of BIA_HORIZON_ALIASES) {
        if (next[canonical] != null && next[canonical] !== "") continue;
        if (isFiniteNumberString(next[alias])) {
            next[canonical] = Number(String(next[alias]).trim());
        }
    }
    if ((next.reqOther == null || next.reqOther === "") && typeof next.other === "string" && next.other !== "") {
        next.reqOther = next.other;
    }
    return next;
}

const optionalLabel = z.preprocess(emptyToUndefined, z.string().optional().nullish());
const optionalQuantity = z.preprocess(
    toOptionalNumber,
    z.number().optional().default(0),
);
const quantityOrLabel = z.preprocess(
    toQuantityOrLabel,
    z.union([z.number(), z.string()]).optional().default(0),
);

export const RecoveryResourcesSchema = z.object({
    id: z.string().optional(),
    name: z.string().min(1, "bcmBcp:form.validation.nameRequired"),
    details: optionalLabel,
    type: optionalLabel,
    linkTo: optionalLabel,
    owner: optionalLabel,
    criticality: optionalLabel,
    limitLevel: optionalLabel,
    normalLevel: quantityOrLabel,
    req2h: optionalQuantity,
    req8h: optionalQuantity,
    req24h: optionalQuantity,
    req2d: optionalQuantity,
    req1w: optionalQuantity,
    reqOther: optionalLabel,
    t2h: optionalLabel,
    t8h: optionalLabel,
    t24h: optionalLabel,
    t2d: optionalLabel,
    t1w: optionalLabel,
    other: optionalLabel,
    availability: quantityOrLabel,
    gap: optionalQuantity,
    contingency: optionalLabel,
    status: z.enum(['active', 'inactive']).optional().default('active'),
    customFields: z.record(z.unknown()).optional(),
});

export const CreateRecoveryResourcesSchema = z.preprocess(
    applyBiaFieldAliases,
    RecoveryResourcesSchema.omit({ id: true }),
);
export const UpdateRecoveryResourcesSchema = z.preprocess(
    applyBiaFieldAliases,
    RecoveryResourcesSchema.partial(),
);
export const RecoveryResourceFormSchema = RecoveryResourcesSchema.extend({
    type: z.string().min(1, "bcmCrisisEvent:detail.cards.recoveryResources.validation.typeRequired"),
});
